import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Calendar, 
  Shield, 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Camera, 
  Loader,
  Target 
} from 'lucide-react';
import axiosInstance from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import './Profile.css';

const Profile = () => {
  const [user, setUser] = useState({
    name: '',
    email: '',
    profilePicture: '',
    isVerified: false,
    createdAt: '',
    authMethod: 'local',
    age: '',
    investmentGoal: ''
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    age: '',
    investmentGoal: ''
  });
  
  const navigate = useNavigate();
  const { logout } = useAuth();

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        logout();
        navigate('/login');
        throw new Error('No authentication token found');
      }

      const response = await axiosInstance.get('/api/users/profile');
      
      if (response.data) {
        const userData = response.data.data || response.data;
        setUser(userData);
        setEditForm({
          name: userData.name || '',
          email: userData.email || '',
          age: userData.age || '',
          investmentGoal: userData.investmentGoal || ''
        });
        setError(null);
      } else {
        throw new Error('Invalid data received from server');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
        return;
      }
      
      setError(error.message || 'Failed to fetch user data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Key changes to fix profile editing
useEffect(() => {
  fetchUserData();
}, []); // Add empty dependency array to prevent infinite re-renders

const handleUpdate = async () => {
  try {
    setLoading(true);
    setError(null);

    // Validate age
    const age = parseInt(editForm.age);
    if (age && (age < 18 || age > 120)) {
      throw new Error('Age must be between 18 and 120');
    }

    const response = await axiosInstance.put('/api/users/profile', {
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      age: editForm.age,
      investmentGoal: editForm.investmentGoal
    });

    if (response.data) {
      // Update user state with new data
      const updatedUser = response.data.data || response.data;
      setUser(prevUser => ({
        ...prevUser,
        ...updatedUser
      }));
      setIsEditing(false);
      
      // Show success notification (you can add a toast here)
      alert('Profile updated successfully');
    }
  } catch (error) {
    console.error('Update error:', error);
    setError(error.response?.data?.message || error.message || 'Failed to update profile. Please try again.');
    alert(error.response?.data?.message || error.message || 'Failed to update profile. Please try again.');
  } finally {
    setLoading(false);
  }
};

// Add better validation
const validateForm = () => {
  const errors = [];
  if (!editForm.name.trim()) errors.push('Name is required');
  if (!editForm.email.trim()) errors.push('Email is required');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) errors.push('Invalid email format');
  if (editForm.age && (parseInt(editForm.age) < 18 || parseInt(editForm.age) > 120)) {
    errors.push('Age must be between 18 and 120');
  }
  return errors;
};

const handleSubmit = async (e) => {
  e.preventDefault();
  const errors = validateForm();
  if (errors.length > 0) {
    setError(errors[0]);
    return;
  }
  await handleUpdate();
};
  const handleEdit = () => {
    setIsEditing(true);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      age: user.age || '',
      investmentGoal: user.investmentGoal || ''
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      age: user.age || '',
      investmentGoal: user.investmentGoal || ''
    });
  };

  const getInvestmentGoalDisplay = (goal) => {
    const goals = {
      'retirement': 'Retirement Planning',
      'money_growth': 'Money Growth',
      'aggressive_growth': 'Aggressive Growth'
    };
    return goals[goal] || 'Not Specified';
  };

  

  const handleUploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('profilePicture', file);

      const response = await axiosInstance.post('/api/users/profile/photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data) {
        const updatedUser = response.data.data || response.data;
        setUser(prevUser => ({
          ...prevUser,
          profilePicture: updatedUser.profilePicture
        }));
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader className="spinner" size={40} />
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <XCircle size={24} />
          <p>{error}</p>
          <button onClick={fetchUserData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <button onClick={() => navigate('/investor')} className="back-button">
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </button>
        <h1>My Profile</h1>
        {!user.isVerified && (
          <Link to="/kyc" className="kyc-button">
            <Shield size={20} />
            <span>Complete KYC</span>
          </Link>
        )}
      </div>

      <div className="profile-content">
        <div className="profile-picture-section">
          <div className="profile-picture-container">
            {user.profilePicture ? (
              <img src={user.profilePicture} alt="Profile" className="profile-picture" />
            ) : (
              <div className="profile-picture-placeholder">
                <User size={40} />
              </div>
            )}
            <input
              type="file"
              id="photo-upload"
              accept="image/*"
              onChange={handleUploadPhoto}
              className="hidden"
            />
            <label htmlFor="photo-upload" className="upload-picture-button">
              <Camera size={20} />
              <span>Update Photo</span>
            </label>
          </div>
        </div>

        <div className="profile-info">
          {isEditing ? (
            <div className="profile-edit-form">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Age</label>
                <input
                  type="number"
                  min="18"
                  max="120"
                  value={editForm.age}
                  onChange={(e) => setEditForm(prev => ({ ...prev, age: e.target.value }))}
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Investment Goal</label>
                <select
                  value={editForm.investmentGoal}
                  onChange={(e) => setEditForm(prev => ({ ...prev, investmentGoal: e.target.value }))}
                  disabled={loading}
                >
                  <option value="">Select a goal</option>
                  <option value="retirement">Retirement Planning</option>
                  <option value="money_growth">Money Growth</option>
                  <option value="aggressive_growth">Aggressive Growth</option>
                </select>
              </div>
              <div className="form-actions">
                <button 
                  onClick={handleUpdate} 
                  className="save-button"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button 
                  onClick={handleCancel} 
                  className="cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="info-group">
                <div className="info-item">
                  <User size={20} />
                  <label>Name</label>
                  <span>{user.name}</span>
                </div>
                <div className="info-item">
                  <Mail size={20} />
                  <label>Email</label>
                  <span>{user.email}</span>
                </div>
                <div className="info-item">
                  <Calendar size={20} />
                  <label>Age</label>
                  <span>{user.age || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <Target size={20} />
                  <label>Investment Goal</label>
                  <span className="goal-tag">
                    {getInvestmentGoalDisplay(user.investmentGoal)}
                  </span>
                </div>
                <div className="info-item">
                  <Calendar size={20} />
                  <label>Member Since</label>
                  <span>{formatDate(user.createdAt)}</span>
                </div>
                <div className="info-item">
                  <Shield size={20} />
                  <label>Verification Status</label>
                  <span className={`status ${user.isVerified ? 'verified' : 'unverified'}`}>
                    {user.isVerified ? (
                      <>
                        <CheckCircle size={16} />
                        Verified
                      </>
                    ) : (
                      <>
                        <XCircle size={16} />
                        Not Verified
                      </>
                    )}
                  </span>
                </div>
              </div>
              <button 
                onClick={handleEdit} 
                className="edit-button"
                disabled={loading}
              >
                Edit Profile
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;